import { useCollaborationValidation, validateImageFile } from "../../../hooks/useCollaborationValidation";
import React, { useState, useRef, useEffect } from "react";
import Sidebar from "../side_bar/SidebarAdmin";
import avatar from "../../../assets/avatar.png";
import { useAuth } from "../../../context/AuthContext";
import { Image, X } from "lucide-react";
import ImageLightbox from "../../../components/common/ImageLightbox";
import "./CollaborateAdmin.css";
import {
  createCollaboration,
  uploadImageToCollaboration,
  deleteCollaboration,
} from "../../../api/colaboracoes";
import {
  CollaborateFormData as ApiFormData,
  ImageWithCode,
} from "../../../api/types";
import Header from "../../../components/common/Header";
import FlashMessage from "../../../components/common/FlashMessage";
import { useFlashMessage } from "../../../hooks/useFlashMessage";
import { AccountPageProps } from "../../../components/common/accountConfig";

interface CollaborateFormData {
  speciesName: string;
  collectionDate: string;
  country: string;
  region: string;
  state: string;
  city: string;
  latitude: string;
  longitude: string;
  altitude: string;
  collector: string;
  collectorInstitution: string;
  photoAuthor: string;
  photoAuthorInstitution: string;
  sibbio: string;
  sisgen: string;
  observations: string;
  folderName: string;
  imageFile?: File;
}

const CollaborateAdmin: React.FC<AccountPageProps> = ({ role = "Admin" }) => {
  const { message, type, showMessage, clearMessage } = useFlashMessage(5000);
  useEffect(() => {
    document.title = "Nova Colaboração";
  }, []);
  const { user, getCsrfToken, getUserIdFromToken } = useAuth();
  const [formData, setFormData] = useState<CollaborateFormData>({
    speciesName: "",
    collectionDate: "",
    country: "",
    region: "",
    state: "",
    city: "",
    latitude: "",
    longitude: "",
    altitude: "",
    collector: "",
    collectorInstitution: "",
    photoAuthor: "",
    photoAuthorInstitution: "",
    sibbio: "",
    sisgen: "",
    observations: "",
    folderName: "",
  });

  const [uploadedImages, setUploadedImages] = useState<ImageWithCode[]>([]);
  const [currentImageCode, setCurrentImageCode] = useState<string>("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewImage, setPreviewImage] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [lightbox, setLightbox] = useState<{
    src: string;
    code?: string;
  } | null>(null);

  const token = getCsrfToken();
  const userId = getUserIdFromToken();

  const validation = useCollaborationValidation(formData, uploadedImages.length, !!selectedFile || !!currentImageCode.trim());

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    validation.change(name, value);
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const fileError = validateImageFile(file);
      if (fileError) {
        validation.rejectImage(fileError);
        showMessage(fileError, "error");
        setSelectedFile(null); setPreviewImage("");
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
      }
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveImage = () => {
    if (!selectedFile || !currentImageCode.trim()) {
      showMessage(
        "Por favor, selecione uma imagem e insira o código antes de salvar.",
        "warning"
      );
      return;
    }

    // Verificar se o código já existe
    const codeExists = uploadedImages.some(
      (img) => img.codigo_imagem === currentImageCode.trim()
    );
    if (codeExists) {
      showMessage(
        "Este código já foi usado para outra imagem. Por favor, use um código diferente.",
        "warning"
      );
      return;
    }

    const newImage: ImageWithCode = {
      id: Date.now().toString(),
      file: selectedFile,
      codigo_imagem: currentImageCode.trim(),
      preview: previewImage,
    };

    validation.change("images", "");
    setUploadedImages((prev) => [...prev, newImage]);

    // Limpar os campos
    setSelectedFile(null);
    setCurrentImageCode("");
    setPreviewImage("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRemoveImage = (id: string) => {
    setUploadedImages((prev) => prev.filter((img) => img.id !== id));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting || !validation.validate(e.currentTarget as HTMLFormElement)) return;
    if (isSubmitting) return;

    if (uploadedImages.length === 0) {
      showMessage(
        "Por favor, adicione pelo menos uma imagem antes de enviar.",
        "warning"
      );
      return;
    }

    setIsSubmitting(true);
    let createdId: string | undefined;

    try {
      // Mapear dados do formulário para o formato da API
      const apiFormData: ApiFormData = {
        nome_especie: formData.speciesName,
        data: formData.collectionDate,
        pais: formData.country,
        regiao: formData.region,
        estado: formData.state,
        municipio: formData.city,
        latitude: formData.latitude,
        longitude: formData.longitude,
        altitude: formData.altitude,
        coletor: formData.collector,
        instituicao_coletor: formData.collectorInstitution,
        autor_foto: formData.photoAuthor,
        instituicao_autor: formData.photoAuthorInstitution,
        SISBIO: formData.sibbio,
        SISGEN: formData.sisgen,
        observacoes: formData.observations,
      };

      // Criar colaboração
      const collaboration = await createCollaboration(
        apiFormData,
        token!,
        userId
      );

      if (!collaboration.colaboracao_id) {
        throw new Error("ID da colaboração não foi retornado pela API");
      }
      createdId = String(collaboration.colaboracao_id);

      // Upload das imagens
      for (const image of uploadedImages) {
        const imageFormData = new FormData();
        imageFormData.append("imagem", image.file); // Campo deve ser "imagem"
        imageFormData.append("codigo_imagem", image.codigo_imagem);

        await uploadImageToCollaboration(
          imageFormData,
          token!,
          collaboration.colaboracao_id
        );
      }

      showMessage("Colaboração criada com sucesso!", "success");
      validation.reset();

      // Reset do formulário
      setFormData({
        speciesName: "",
        collectionDate: "",
        country: "",
        region: "",
        state: "",
        city: "",
        latitude: "",
        longitude: "",
        altitude: "",
        collector: "",
        collectorInstitution: "",
        photoAuthor: "",
        photoAuthorInstitution: "",
        sibbio: "",
        sisgen: "",
        observations: "",
        folderName: "",
      });
      setUploadedImages([]);
      setCurrentImageCode("");
      setSelectedFile(null);
      setPreviewImage("");
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (error) {
      console.error("Erro ao criar colaboração:", error);
      validation.reportServerError(error);
      // Preserve the rollback already used by common/contributor submissions.
      if (role !== "Admin" && createdId) {
        try { await deleteCollaboration(createdId, token || ""); }
        catch { showMessage("O envio ficou incompleto. Confira Minhas solicitações antes de tentar novamente.", "warning"); }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const fieldGroups: {
    title: string;
    fields: { name: keyof CollaborateFormData; label: string; type?: string; placeholder?: string; wide?: boolean }[];
  }[] = [
    { title: "Dados da coleta", fields: [
      { name: "speciesName", label: "Nome da espécie", placeholder: "Nome da espécie" },
      { name: "collectionDate", label: "Data da coleta", type: "date" },
    ] },
    { title: "Localização", fields: [
      { name: "country", label: "País", placeholder: "País da coleta" },
      { name: "region", label: "Região", placeholder: "Região da coleta" },
      { name: "state", label: "Estado", placeholder: "Estado da coleta" },
      { name: "city", label: "Município", placeholder: "Município da coleta" },
      { name: "latitude", label: "Latitude", placeholder: "Latitude" },
      { name: "longitude", label: "Longitude", placeholder: "Longitude" },
      { name: "altitude", label: "Altitude (m)", placeholder: "Altitude em metros" },
    ] },
    { title: "Autoria e autorizações", fields: [
      { name: "collector", label: "Coletor", placeholder: "Nome do coletor" },
      { name: "collectorInstitution", label: "Instituição do coletor", placeholder: "Instituição" },
      { name: "photoAuthor", label: "Autor da foto", placeholder: "Nome do autor" },
      { name: "photoAuthorInstitution", label: "Instituição do autor da foto", placeholder: "Instituição" },
      { name: "sibbio", label: "SISBIO", placeholder: "Número da autorização" },
      { name: "sisgen", label: "SISGEN", placeholder: "Número do cadastro" },
    ] },
  ];

  const resetForm = () => {
    validation.reset();
    setFormData({
      speciesName: "", collectionDate: "", country: "", region: "", state: "",
      city: "", latitude: "", longitude: "", altitude: "", collector: "",
      collectorInstitution: "", photoAuthor: "", photoAuthorInstitution: "",
      sibbio: "", sisgen: "", observations: "", folderName: "",
    });
    setUploadedImages([]);
    setCurrentImageCode("");
    setSelectedFile(null);
    setPreviewImage("");
    setLightbox(null);
    clearMessage();
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <div className="account-profile-page account-collaborate-page">
      <Header />
      {message && (
        <div className="collaboration-feedback">
          <FlashMessage message={message} type={type} onClose={clearMessage} />
        </div>
      )}
      <div className="account-profile-layout">
        <div className="account-navigation">
          <Sidebar role={role} activeItem="colaborar" avatar={avatar} name={user?.nome_completo} />
        </div>

        <main className="account-profile-content">
          <div className="account-page-heading">
            <h1>Colaborar com imagens</h1>
          </div>
          <form onSubmit={handleSubmit} noValidate aria-label="Nova colaboração" aria-busy={isSubmitting}>
            <p className="collaboration-form-hint">Campos com <span className="collaboration-required">*</span> são obrigatórios. Adicione ao menos uma imagem com código.</p>
            {validation.summary}
            <fieldset className="collaboration-sections" disabled={isSubmitting}>
              {fieldGroups.map((group, groupIndex) => (
                <section className="account-panel" key={group.title} aria-labelledby={`collaboration-section-${groupIndex}`}>
                  <div className="account-panel-heading">
                    <h2 id={`collaboration-section-${groupIndex}`}>{group.title}</h2>
                  </div>
                  <div className="account-fields">
                    {group.fields.map((field) => (
                      <div className="account-field" key={field.name}>
                        <label className="form-label" htmlFor={`collaboration-${field.name}`}>{field.label}{validation.field(field.name).required ? <span className="collaboration-required" aria-hidden="true"> *</span> : null}</label>
                        <input
                          id={`collaboration-${field.name}`}
                          name={field.name}
                          placeholder={field.placeholder}
                          value={String(formData[field.name] || "")}
                          onChange={handleChange}
                          {...validation.field(field.name)}
                        />
                        {validation.errorFor(field.name)}
                      </div>
                    ))}
                  </div>
                </section>
              ))}

              <section className="account-panel" aria-labelledby="collaboration-images-title">
                <div className="account-panel-heading collaboration-images-heading">
                  <h2 id="collaboration-images-title">Imagens</h2>
                  <span className="collaboration-count">{uploadedImages.length} adicionada(s)</span>
                </div>

                <details className="collaboration-naming">
                  <summary>Padrão de identificação das imagens</summary>
                  <p>Para aprovação, nomeie os arquivos conforme o padrão <strong>A1C1AD</strong>: A1 = abelha (indivíduo), C1 = colônia, A = asa e D = direita.</p>
                </details>

                <div className="collaboration-upload">
                  <div className="collaboration-preview">
                    {previewImage ? (
                      <button type="button" className="collaboration-preview-button" aria-label="Ampliar imagem selecionada" onClick={() => setLightbox({ src: previewImage, code: currentImageCode })}>
                        <img src={previewImage} alt={currentImageCode || "Imagem selecionada"} />
                      </button>
                    ) : (
                      <div className="collaboration-placeholder">
                        <Image size={36} aria-hidden="true" />
                        <span>Prévia da imagem</span>
                      </div>
                    )}
                  </div>
                  <div className="collaboration-upload-fields">
                    <div className="account-field">
                      <label className="form-label" htmlFor="collaboration-image-code">Código da imagem</label>
                      <input
                        id="collaboration-image-code"
                        type="text"
                        className="form-control"
                        placeholder="Ex.: A1C1AD"
                        value={currentImageCode}
                        data-collaboration-images
                        aria-label="Código da imagem"
                        autoCapitalize="characters"
                        spellCheck={false}
                        onChange={(e) => setCurrentImageCode(e.target.value.toUpperCase())}
                      />
                    </div>
                    <div className="account-field">
                      <label className="form-label" htmlFor="collaboration-image-file">Arquivo da imagem</label>
                      <input
                        id="collaboration-image-file"
                        type="file"
                        aria-label="Arquivo da imagem"
                        ref={fileInputRef}
                        className="form-control"
                        onChange={handleImageUpload}
                        accept="image/jpeg,image/png,image/webp,image/gif,image/avif,image/bmp,image/tiff"
                      />
                      <small className="collaboration-file-hint">Até 10 MB por imagem. Preencha o código, selecione o arquivo e clique em Adicionar/Salvar imagem.</small>
                      {validation.errorFor("images")}
                    </div>
                    <button type="button" className="btn btn-outline-primary" onClick={handleSaveImage} disabled={!selectedFile || !currentImageCode.trim()}>
                      Adicionar imagem
                    </button>
                  </div>
                </div>

                {uploadedImages.length > 0 && (
                  <ul className="collaboration-image-list" aria-label="Imagens adicionadas">
                    {uploadedImages.map((image) => (
                      <li className="collaboration-image-card" key={image.id}>
                        <button type="button" className="collaboration-thumbnail" aria-label={`Ampliar imagem ${image.codigo_imagem}`} onClick={() => setLightbox({ src: image.preview, code: image.codigo_imagem })}>
                          <img src={image.preview} alt={image.codigo_imagem} />
                        </button>
                        <div className="collaboration-image-info">
                          <strong>{image.codigo_imagem}</strong>
                          <span title={image.file.name}>{image.file.name}</span>
                        </div>
                        <button type="button" className="collaboration-remove" aria-label={`Remover imagem ${image.codigo_imagem}`} onClick={() => handleRemoveImage(image.id)}>
                          <X size={18} />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              <section className="account-panel" aria-labelledby="collaboration-observations-title">
                <div className="account-panel-heading"><h2 id="collaboration-observations-title">Observações</h2></div>
                <textarea
                  name="observations"
                  aria-labelledby="collaboration-observations-title"
                  className="form-control"
                  rows={4}
                  placeholder="Observações sobre a coleta ou as imagens"
                  value={formData.observations}
                  onChange={handleChange}
                />
                <div className="account-form-actions">
                  <button type="button" className="btn btn-outline-secondary" onClick={resetForm}>Cancelar</button>
                  <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                    {isSubmitting ? "Enviando…" : "Solicitar colaboração"}
                  </button>
                </div>
              </section>
            </fieldset>
          </form>
        </main>
      </div>
      {lightbox && (
        <ImageLightbox src={lightbox.src} code={lightbox.code} alt="Imagem" onClose={() => setLightbox(null)} />
      )}
    </div>
  );
};

export default CollaborateAdmin;
